import React from "react";

import {
  AlertCircle,
  Link2,
  type LucideIcon,
  Minus,
  TrendingDown,
  TrendingUp,
  Volume2,
  X,
} from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../intelligence-briefing-library/components/ui/tooltip";
import { BAND_FILL, bandOf } from "../lib/derive";
import type {
  XFactorKpiFullBriefing,
  XFactorKpiMetricRef,
} from "../lib/deriveKpi";

/**
 * Building blocks for the KPI bubble detail modal, ported from xfactor's
 * bubble-chart-kpi render-blocks (primitives, EvidenceCitation, hero bubble,
 * action cluster, audio button). Band colours are the matrix's BAND_FILL,
 * so the hero bubble matches the bubble that was clicked.
 */

/* ------------------------------------------------------------------ */
/* Score colours and formatting                                         */
/* ------------------------------------------------------------------ */

export const NO_SCORE_HEX = "#94a3b8";

/** The matrix bubbles' own band colours, so the hero matches the bubble clicked. */
export const scoreHex = (score: number | null | undefined): string => {
  if (score == null || !Number.isFinite(score)) return NO_SCORE_HEX;
  return BAND_FILL[bandOf(score)];
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** `2026-06-01` → `01-Jun-2026`, xfactor's formatDateDMY. */
export const formatDMY = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}-${MONTHS[Number(m[2]) - 1] ?? m[2]}-${m[1]}`;
};

const trimZeros = (s: string): string =>
  s.indexOf(".") >= 0 ? s.replace(/\.?0+$/, "") : s;

/**
 * xfactor's formatMagnitude: two decimals under 1,000; above it one decimal
 * while the scaled value is under 10 (5.4M), whole units above (671M).
 */
export const formatMagnitude = (n: number): string => {
  const abs = Math.abs(n);
  if (abs < 1000) return trimZeros(n.toFixed(2));
  const units: Array<[number, string]> = [
    [1e12, "T"],
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "K"],
  ];
  for (const [div, suffix] of units) {
    if (abs >= div) {
      const scaled = n / div;
      const body =
        Math.abs(scaled) < 10
          ? trimZeros(scaled.toFixed(1))
          : String(Math.round(scaled));
      return `${body}${suffix}`;
    }
  }
  return trimZeros(n.toFixed(2));
};

/**
 * An actual metric value with its unit — xfactor's formatSmartValueWithUom:
 * `$671M`, `92.63%`, `10.43 Hours`. Large values always get a magnitude, so
 * a raw 670909622.34 never reaches the screen.
 */
export const formatActual = (
  value: number | null | undefined,
  uom: string | null | undefined
): string => {
  if (value == null || !Number.isFinite(value)) return "—";
  const u = (uom || "").trim();
  if (/^(usd|\$)$/i.test(u)) {
    return `${value < 0 ? "-" : ""}$${formatMagnitude(Math.abs(value))}`;
  }
  const body = formatMagnitude(value);
  if (!u) return body;
  return u === "%" ? `${body}%` : `${body} ${u}`;
};

/** Strip stray `**` bold markers and trailing citation runs like `[2][5]`. */
export const cleanNarrative = (s: string | null | undefined): string =>
  (s || "")
    .replace(/\*\*/g, "")
    .replace(/(?:\s*\[[^\]]+\])+\s*$/g, "")
    .trim();

/* ------------------------------------------------------------------ */
/* Tooltip                                                              */
/* ------------------------------------------------------------------ */

/** xfactor's dark tooltip. Radix, so it portals above the modal. */
export const Tip: React.FC<{
  content: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  /** The header buttons' one-word label pill. */
  pill?: boolean;
  children: React.ReactElement;
}> = ({ content, side = "top", pill, children }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side={side}
        className={
          pill
            ? "rounded-full border-white/10 bg-slate-900/90 px-3 py-1.5 text-[10px] font-medium tracking-wide text-white shadow-2xl backdrop-blur-md"
            : "max-w-xs border-slate-900 bg-slate-900 px-2.5 py-1.5 text-[11px] text-white"
        }
      >
        {content}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/* ------------------------------------------------------------------ */
/* Trend glyph                                                          */
/* ------------------------------------------------------------------ */

export type TrendKind = "up" | "down" | "flat" | null;

export const classifyTrend = (
  direction: string | null | undefined
): TrendKind => {
  if (!direction) return null;
  const d = direction.toLowerCase();
  if (/(up|improv|increas|rising|positive|better)/.test(d)) return "up";
  if (/(down|declin|decreas|worsen|falling|negative)/.test(d)) return "down";
  if (/(flat|stable|steady|unchanged|neutral)/.test(d)) return "flat";
  return null;
};

/** `Trend: ↑ 12%` — the tooltip line under a dot. */
export const trendLine = (
  direction: string | null | undefined,
  magnitudePct: number | null | undefined
): string | null => {
  const kind = classifyTrend(direction);
  if (!kind) return null;
  const arrow = kind === "up" ? "↑" : kind === "down" ? "↓" : "→";
  const mag =
    magnitudePct != null && Number.isFinite(magnitudePct)
      ? ` ${Math.abs(magnitudePct).toFixed(1)}%`
      : "";
  return `Trend: ${arrow}${mag}`;
};

export const TrendGlyph: React.FC<{ direction: string | null | undefined }> = ({
  direction,
}) => {
  const kind = classifyTrend(direction);
  if (!kind) return null;
  if (kind === "up")
    return (
      <TrendingUp className="h-3 w-3 shrink-0 text-emerald-600" aria-hidden />
    );
  if (kind === "down")
    return (
      <TrendingDown className="h-3 w-3 shrink-0 text-rose-600" aria-hidden />
    );
  return <Minus className="h-3 w-3 shrink-0 text-slate-500" aria-hidden />;
};

/* ------------------------------------------------------------------ */
/* Hero bubble                                                          */
/* ------------------------------------------------------------------ */

/**
 * xfactor's HeroScoreBubble: a light outer disc, a solid inner disc in the
 * band colour, and the score in a darker badge at the top right.
 */
export const HeroScoreBubble: React.FC<{ score: number | null }> = ({
  score,
}) => {
  const solid = scoreHex(score);
  return (
    <div
      className="relative h-12 w-12 shrink-0"
      aria-label={`Score ${score == null ? "not available" : Math.round(score)}`}
    >
      <div
        className="absolute inset-0 rounded-full"
        style={{ backgroundColor: `color-mix(in oklab, ${solid} 22%, white)` }}
      />
      <div
        className="absolute rounded-full"
        style={{
          backgroundColor: solid,
          top: "22%",
          left: "22%",
          right: "22%",
          bottom: "22%",
        }}
      />
      <div
        className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white ring-2 ring-white"
        style={{ backgroundColor: `color-mix(in oklab, ${solid} 85%, black)` }}
      >
        {score == null ? "—" : Math.round(score)}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Header action cluster: Audio + Explainability                        */
/* ------------------------------------------------------------------ */

/** Glass pill holding the header's round action buttons. */
export const ActionCluster: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const items = React.Children.toArray(children).filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="flex shrink-0 items-center rounded-[2rem] border border-white/60 bg-white/40 p-1 shadow-[0_4px_18px_rgba(15,23,42,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] backdrop-blur-2xl transition-all duration-500 hover:rounded-[1.1rem] hover:shadow-[0_14px_36px_rgba(124,58,237,0.18)]">
      {items.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 ? (
            <div
              aria-hidden
              className="mx-1.5 h-5 w-px bg-gradient-to-b from-transparent via-slate-400/40 to-transparent"
            />
          ) : null}
          {item}
        </React.Fragment>
      ))}
    </div>
  );
};

const ROUND_BUTTON =
  "inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-700 transition-all duration-300 hover:scale-105 hover:bg-white/70 hover:text-purple-600 hover:shadow-[0_0_18px_rgba(255,255,255,0.75)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/40 active:scale-95";

/**
 * Reads `text` aloud with the browser's own speech synthesis, as xfactor
 * does — no server TTS. Clicking again while speaking stops it.
 */
export const AudioButton: React.FC<{ text: string | null }> = ({ text }) => {
  const [speaking, setSpeaking] = React.useState(false);

  React.useEffect(() => {
    // New text (another pane) or unmount: stop whatever is playing.
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      setSpeaking(false);
    };
  }, [text]);

  if (!text || typeof window === "undefined" || !window.speechSynthesis) {
    return null;
  }

  const toggle = () => {
    const synth = window.speechSynthesis;
    synth.cancel();
    if (speaking) {
      setSpeaking(false);
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    setSpeaking(true);
    synth.speak(utterance);
  };

  return (
    <Tip pill content={speaking ? "Stop audio" : "Audio"}>
      <button
        type="button"
        onClick={toggle}
        aria-label={speaking ? "Stop audio" : "Audio"}
        aria-pressed={speaking}
        className={`${ROUND_BUTTON}${speaking ? " text-purple-600" : ""}`}
      >
        <Volume2 size={18} strokeWidth={2} />
      </button>
    </Tip>
  );
};

/** xfactor's four-point sparkle: rotates and lights its centre on hover. */
export const ExplainabilityButton: React.FC<{ onClick: () => void }> = ({
  onClick,
}) => (
  <Tip pill content="Explainability">
    <button
      type="button"
      onClick={onClick}
      aria-label="Explainability"
      className={`group relative ${ROUND_BUTTON}`}
    >
      <svg
        viewBox="0 0 24 24"
        width={18}
        height={18}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="transition-transform duration-500 group-hover:rotate-[30deg]"
        aria-hidden
      >
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
        <circle
          cx="12"
          cy="12"
          r="2"
          fill="currentColor"
          className="opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        />
      </svg>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full bg-purple-400/0 blur-xl transition-all duration-500 group-hover:bg-purple-400/25"
      />
    </button>
  </Tip>
);

/* ------------------------------------------------------------------ */
/* Nested overlay keyboard guard                                        */
/* ------------------------------------------------------------------ */

/**
 * While a nested overlay is open, Escape closes only it and the arrow keys
 * don't step the detail modal underneath. The modal listens on `window` in
 * the bubble phase; a capture listener on `document` runs first and stops
 * the event from getting there.
 */
export const useNestedOverlayKeys = (open: boolean, onClose: () => void) => {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      } else if (e.key.startsWith("Arrow")) {
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);
};

/* ------------------------------------------------------------------ */
/* Evidence citation                                                    */
/* ------------------------------------------------------------------ */

export type KpiMetricLookupEntry = {
  label: string;
  value: number | null;
  normalizedScore: number | null;
  uom: string | null;
};

export type KpiMetricLookup = (metricId: string) => KpiMetricLookupEntry | null;

/** Briefing-wide metric id → name and values, so a citation from any pane resolves. */
export const buildMetricLookup = (
  briefing: XFactorKpiFullBriefing | null
): KpiMetricLookup => {
  const byId = new Map<string, KpiMetricLookupEntry>();
  const ingest = (
    metrics: XFactorKpiFullBriefing["categories"][number]["metrics"]
  ) => {
    metrics.forEach(m => {
      if (!m.metricId || byId.has(m.metricId)) return;
      byId.set(m.metricId, {
        label: m.name || m.metricId,
        value: m.value,
        normalizedScore: m.normalizedValue,
        uom: m.uom,
      });
    });
  };
  if (briefing?.overall) ingest(briefing.overall.metrics);
  briefing?.categories.forEach(c => ingest(c.metrics));
  return id => byId.get(id) ?? null;
};

type EvidenceRow = {
  key: string;
  label: string;
  value: string | null;
  normalized: string | null;
  period: string | null;
};

/**
 * The link icon at the right of each block title. Purple and glowing when the
 * block cites metrics that resolve to a real value; grey and inert otherwise.
 * Opens "Evidence citation": `metric = value (norm.: N) dated period`.
 */
export const EvidenceCitation: React.FC<{
  title: string;
  refs: XFactorKpiMetricRef[];
  lookup: KpiMetricLookup;
  measurementDate?: string | null;
}> = ({ title, refs, lookup, measurementDate }) => {
  const [open, setOpen] = React.useState(false);
  const close = React.useCallback(() => setOpen(false), []);
  useNestedOverlayKeys(open, close);

  const seen = new Set<string>();
  const rows: EvidenceRow[] = [];
  refs.forEach(ref => {
    const key = `${ref.metricId}::${ref.period ?? ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    const entry = lookup(ref.metricId);
    const value =
      entry?.value != null ? formatActual(entry.value, entry.uom) : null;
    const normalized =
      entry?.normalizedScore != null
        ? String(Math.round(entry.normalizedScore))
        : null;
    // A ref with no concrete measurement behind it is noise, not evidence.
    if (value == null && normalized == null) return;
    rows.push({
      key,
      label: entry?.label ?? ref.metricId,
      value,
      normalized,
      period:
        ref.period === "trend"
          ? "trend"
          : ref.period || (measurementDate ? formatDMY(measurementDate) : null),
    });
  });
  const hasEvidence = rows.length > 0;

  return (
    <>
      <Tip
        content={
          hasEvidence
            ? "Click to see evidence citation from related metrics"
            : "No supporting evidence linked"
        }
      >
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            if (hasEvidence) setOpen(true);
          }}
          aria-label={
            hasEvidence
              ? `Evidence citation for ${title}`
              : `No supporting evidence linked for ${title}`
          }
          aria-disabled={!hasEvidence}
          className={
            hasEvidence
              ? "inline-flex h-5 w-5 items-center justify-center rounded text-purple-500 drop-shadow-[0_0_4px_rgba(168,85,247,0.65)] hover:bg-purple-100/70 hover:text-purple-600"
              : "inline-flex h-5 w-5 cursor-not-allowed items-center justify-center rounded text-gray-300"
          }
        >
          <Link2 className="h-3.5 w-3.5" />
        </button>
      </Tip>

      {open ? (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/30 p-4"
          role="presentation"
          onClick={close}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Evidence citation"
            className="w-fit max-w-[95vw] rounded-xl bg-white p-5 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between gap-6">
              <h3 className="text-base font-semibold text-purple-600">
                Evidence citation
              </h3>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="inline-flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <X size={16} />
              </button>
            </div>
            <p className="mb-2 text-sm text-gray-500">
              The <span className="font-semibold text-gray-900">{title}</span>{" "}
              is based on:
            </p>
            <div className="overflow-hidden rounded-md border border-gray-200">
              {rows.map((r, i) => (
                <div
                  key={r.key}
                  className={`flex items-start gap-2 px-2 py-1.5 text-[13px] leading-relaxed ${
                    i % 2 === 0 ? "bg-gray-50" : ""
                  }`}
                >
                  <span
                    aria-hidden
                    className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-purple-500"
                  />
                  <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                    <span className="font-semibold">{r.label}</span>
                    {r.value != null ? (
                      <>
                        {" "}
                        = <span className="tabular-nums">{r.value}</span>
                      </>
                    ) : null}
                    {r.normalized != null ? (
                      <span className="text-gray-500">
                        {" "}
                        (norm.: {r.normalized})
                      </span>
                    ) : null}
                    {r.period ? (
                      <span className="text-gray-500"> dated {r.period}</span>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Block card                                                           */
/* ------------------------------------------------------------------ */

export type BlockTone =
  | "blue"
  | "emerald"
  | "rose"
  | "teal"
  | "purple"
  | "cyan";

const TONE: Record<BlockTone, { card: string; title: string; icon: string }> = {
  blue: {
    card: "bg-blue-50/70",
    title: "text-blue-700",
    icon: "text-blue-600",
  },
  emerald: {
    card: "bg-emerald-50/70",
    title: "text-emerald-700",
    icon: "text-emerald-600",
  },
  rose: {
    card: "bg-rose-50/70",
    title: "text-rose-700",
    icon: "text-rose-600",
  },
  teal: {
    card: "bg-teal-50/70",
    title: "text-teal-700",
    icon: "text-teal-600",
  },
  purple: {
    card: "border-purple-200 bg-purple-50/40",
    title: "text-purple-700",
    icon: "text-purple-600",
  },
  cyan: {
    card: "bg-cyan-50/70",
    title: "text-cyan-700",
    icon: "text-cyan-600",
  },
};

/** A tinted, titled section card — every body block in the modal is one. */
export const BlockCard: React.FC<{
  id: string;
  tone: BlockTone;
  icon: LucideIcon;
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}> = ({ id, tone, icon: Icon, title, right, children }) => {
  const t = TONE[tone];
  return (
    <div
      id={id}
      className={`scroll-mt-2 rounded-md border border-gray-200 p-3 text-sm ${t.card}`}
    >
      <div className="mb-2 flex items-center gap-2">
        <Icon className={`h-4 w-4 ${t.icon}`} aria-hidden />
        <h3 className={`font-semibold ${t.title}`}>{title}</h3>
        {right ? <div className="ml-auto">{right}</div> : null}
      </div>
      <div className="text-gray-800">{children}</div>
    </div>
  );
};

/** Muted italic note for a block with nothing to show. */
export const EmptyNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <p className="text-[12px] italic text-gray-500">{children}</p>;

/** Numbered list of item cards inside a block. */
export const NumberedList: React.FC<{
  items: React.ReactNode[];
}> = ({ items }) => (
  <ol className="m-0 grid list-none gap-2 p-0">
    {items.map((item, i) => (
      <li key={i} className="rounded-md border border-gray-200 bg-white/60 p-2">
        <div className="flex items-start gap-2">
          <div className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border border-gray-300 text-[10.5px] font-semibold">
            {i + 1}
          </div>
          <div className="min-w-0 flex-1">{item}</div>
        </div>
      </li>
    ))}
  </ol>
);

/** Small outline tag after an item's text (horizon, action score, trend source). */
export const ItemTag: React.FC<{
  tone?: "amber";
  children: React.ReactNode;
}> = ({ tone, children }) => (
  <span
    className={`inline-flex h-4 items-center rounded-full border px-1.5 text-[10px] ${
      tone === "amber"
        ? "border-amber-500 text-amber-700"
        : "border-gray-300 text-gray-600"
    }`}
  >
    {children}
  </span>
);

/** Amber chip marking an item whose supporting data was flagged anomalous. */
export const AnomalyChip: React.FC<{
  anomaly: { flag: boolean; type: string | null; reason: string | null };
}> = ({ anomaly }) => {
  if (!anomaly.flag) return null;
  return (
    <Tip
      content={
        anomaly.reason ?? anomaly.type ?? "Data quality anomaly flagged."
      }
    >
      <span className="inline-flex h-4 items-center gap-1 rounded-full border border-amber-500 px-1 text-[9.5px] text-amber-700">
        <AlertCircle className="h-2.5 w-2.5" aria-hidden />
        {anomaly.type ?? "anomaly"}
      </span>
    </Tip>
  );
};
